import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import {
  useDeactivateMember,
  useMember,
  useResendInvite,
  useUpdateMember,
} from '@/hooks/use-members'
import { MemberProfileView } from '@/components/profile/member-profile-view'
import type { ManageableMemberStatus, MemberDetail } from '@riverpark/types'

export interface MemberDetailConfig {
  memberId: string
  listPath: string
  canDelete: boolean
  canManageRole: boolean
  canManagePlots: boolean
  /** Base path for plot detail links, e.g. /operations/plots */
  plotsBasePath?: string
  /** Hide edit controls when viewing another member (estate manager). */
  readOnly?: boolean
}

export function MemberDetailPage({
  memberId,
  listPath,
  canDelete,
  canManageRole,
  canManagePlots,
  plotsBasePath,
  readOnly = false,
}: MemberDetailConfig) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isLoading, error } = useMember(memberId)
  const updateMember = useUpdateMember()
  const deactivateMember = useDeactivateMember()
  const resendInvite = useResendInvite()

  const member = data?.data
  const isOwnProfile = user?.id === memberId

  async function handleSave(payload: {
    name: string
    email: string
    phone: string
    role: MemberDetail['role']
    status?: ManageableMemberStatus
  }) {
    const body: Parameters<typeof updateMember.mutateAsync>[0]['body'] = {
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
    }
    // API forbids role/status on self — admin profile uses this route via topbar.
    if (canManageRole && !isOwnProfile) {
      body.role = payload.role
      if (payload.status !== undefined) {
        body.status = payload.status
      }
    }
    await updateMember.mutateAsync({ id: memberId, body })
  }

  async function handleDeactivate() {
    const msg =
      member?.status === 'invited'
        ? 'Cancel this invitation? The member will be marked inactive and cannot sign in until reactivated.'
        : 'Deactivate this member? They will no longer be able to sign in.'
    if (!confirm(msg)) return

    let reason: string | undefined
    if (user?.role === 'estate_manager') {
      const input = prompt(
        'Reason for deactivation (sent to committee/admin for approval — nothing happens until they approve it):',
      )
      if (!input?.trim()) return
      reason = input.trim()
    }

    const result = await deactivateMember.mutateAsync({ id: memberId, reason })
    if (result.data.pendingApproval) {
      alert('Deactivation request sent for approval.')
      return
    }
    navigate(listPath)
  }

  async function handleResendInvite() {
    await resendInvite.mutateAsync(memberId)
  }

  return (
    <MemberProfileView
      profile={member}
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      isSelf={isOwnProfile}
      backLink={{ to: listPath, label: 'Members' }}
      canManageRole={canManageRole && !isOwnProfile}
      canManagePlots={canManagePlots && !isOwnProfile}
      plotsBasePath={plotsBasePath}
      canDelete={canDelete && !isOwnProfile}
      readOnly={readOnly && !isOwnProfile}
      onSave={handleSave}
      onDeactivate={canDelete ? handleDeactivate : undefined}
      onResendInvite={canManageRole ? handleResendInvite : undefined}
      isSavePending={updateMember.isPending}
      isDeactivatePending={deactivateMember.isPending}
      isResendPending={resendInvite.isPending}
    />
  )
}
